import {
  createReportEventUnauthenticatedParams,
  ReportEventUnauthenticatedParams,
  TelemetryForwarder,
  ValidPropertyValueType
} from '@tableau/typescript-telemetry-forwarder';
import { defaultMemoize } from 'reselect';
import { v4 } from 'uuid';
import { TelemetryContext } from './TelemetryContext';

export type BaseTelemetryEvent = { type: string };
export type AnalyticsEventProperties = { [K: string]: ValidPropertyValueType };

/**
 * Only specific types (validPropertyValueType) are allowed as values for events.
 * Custom telemetry event interfaces can extend this type to ensure that type-safety.
 */
type ValidTelemetryEventInternal<T extends BaseTelemetryEvent, U extends keyof T> = {
  [K in U]: T[K] extends ValidPropertyValueType ? T[K] : never
};
export type ValidTelemetryEvent<T extends BaseTelemetryEvent> = ValidTelemetryEventInternal<T, keyof T>;

class TelemetrySender {
  /**
   * TelemetryService handles sending telemetry events to the Google Big Query database.
   *
   * App-wide event properties get set through the reporter and eventFactory
   * Usage examples: UrlTelemetryService.ts for Angular and SiteSpace.tsx for React
   *
   * For setting up events in Google BigQuery, see https://mytableau.tableaucorp.com/x/0hOHBw
   */

  public sendEvent<T extends BaseTelemetryEvent>(event: ValidTelemetryEvent<T>, telemetryContext: TelemetryContext): void {
    const { type, ...customProps } = event as BaseTelemetryEvent & AnalyticsEventProperties;

    const properties: AnalyticsEventProperties = {
      ...customProps,
      ...this.getCommonProperties(telemetryContext)
    };

    Object.keys(properties).forEach(key => {
      // since remove waitingForSession or getSessionInfo before we send telemetry, values of sessionInfo could be null
      // vizportal java code does not allow null value, so need convert to empty string
      if (properties[key] === undefined || properties[key] === null) {
        properties[key] = '';
      }
    });

    // java-event-client use NetworkUtilities.getHostName() to get hostName, so we do not need to add hostName
    // tslint:disable-next-line:max-line-length
    // https://gitlab.tableausoftware.com/Product-Analytics/java-event-client/blob/master/src/main/java/com/tableau/telemetry/event/AEvent.java#L27
    const params: ReportEventUnauthenticatedParams = createReportEventUnauthenticatedParams(type, 'datacatalog', properties);

    // When called from Hybrid UI on Desktop and Server, we can't send telemetry directly and instead have to
    // use the callback they provide.
    if (this.telemetryContractApiCallback) {
      const payload: ValidPropertyValueType[] = new Array<ValidPropertyValueType>();

      Object.keys(properties).forEach(key => {
        // push as a "name value pair"
        payload.push(key, properties[key]);
      });

      this.telemetryContractApiCallback(type, payload);
    } else {
      this.getTelemetryForwarder().send(params);
    }}

  private getCommonProperties(telemetryContext: TelemetryContext): AnalyticsEventProperties {
    return {
      uuid: v4(),
      telemetryEnvironment: telemetryContext.telemetryEnvironment || '',
      siteLuid: telemetryContext.siteLuid || '',
      dialogReleaseVersion: telemetryContext.dialogReleaseVersion || '',
      appSessionId: telemetryContext.appSessionId || '',
      dialogSessionId: telemetryContext.dialogSessionId || '',
    };
  }

  // telemetry-contract-api requires this particular signature for its callback
  // eslint-disable-next-line @typescript-eslint/ban-types
  private telemetryContractApiCallback: ((eventType: string, payload: {}[]) => void) | undefined = undefined;
  // eslint-disable-next-line @typescript-eslint/ban-types
  public setTelemetryContractApiCallback = (callback: ((eventType: string, payload: {}[]) => void)): void => {
    this.telemetryContractApiCallback = callback;
  }

  private getTelemetryForwarder = defaultMemoize(() => new TelemetryForwarder());
}

export const telemetrySender: TelemetrySender = new TelemetrySender();
